/* ==========================================================================
   Meat.Kitchen — menu system
   - One menu draft shared by the Build page and product pages (kept in browser storage).
   - The cart only changes when the menu reaches the minimum (soft gate) and
     the customer presses "Add to cart" / "Update cart".
   - Product pages never add to cart; they send the customer to the Build page
     with ?add=<handle>, which adds one pouch to the draft.
   Port target: assets/mk-menu.js (loaded after mk-theme.js)
   ========================================================================== */
(function () {
  'use strict';

  var MK = window.MK || {};
  var MIN = parseInt(MK.minCount, 10) || 0;
  var KEY = 'mk:menu:v1';
  var MAX_AGE = 1000 * 60 * 60 * 24 * 21; /* forget an untouched draft after 3 weeks */
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var LABELS = MK.menuLabels || {};
  var listeners = [];

  /* ---------- helpers ---------- */
  function fill(template, data) {
    return String(template || '').replace(/\[(remaining|min|count)\]/g, function (_, k) { return data[k]; });
  }
  function money(cents) {
    var amount = (Math.round(cents) / 100).toFixed(2);
    var format = MK.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount\s*\}\}/, amount.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  }
  function plural(n, one, many) { return n === 1 ? one : many; }
  function sameMap(a, b) {
    a = a || {}; b = b || {};
    var keys = {};
    Object.keys(a).forEach(function (k) { if (a[k] > 0) keys[k] = 1; });
    Object.keys(b).forEach(function (k) { if (b[k] > 0) keys[k] = 1; });
    return Object.keys(keys).every(function (k) { return (a[k] || 0) === (b[k] || 0); });
  }
  function sum(map) {
    return Object.keys(map || {}).reduce(function (n, k) { return n + (map[k] > 0 ? map[k] : 0); }, 0);
  }
  /* @storage-start — the theme keeps the draft in localStorage; the design preview swaps this block for its own store. */
  var STORE = (function () {
    try {
      var ls = window.localStorage;
      ls.setItem('__mk', '1');
      ls.removeItem('__mk');
      return ls;
    } catch (e) { return null; }
  })();
  /* @storage-end */
  function readStore() {
    if (!STORE) return null;
    try {
      var s = JSON.parse(STORE.getItem(KEY) || 'null');
      if (!s || s.v !== 1 || (Date.now() - (s.ts || 0)) > MAX_AGE) return null;
      return s;
    } catch (e) { return null; }
  }
  function writeStore() {
    if (!STORE) return;
    try {
      STORE.setItem(KEY, JSON.stringify({
        v: 1, ts: Date.now(), items: state.items, order: state.order, meta: state.meta, plan: state.plan, base: state.base, basePlan: state.basePlan
      }));
    } catch (e) { /* private mode: the draft simply lives for this page view */ }
  }
  function jsonFetch(path, body) {
    var opts = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    if (body) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(ROOT + path, opts).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok || data.status >= 400) {
          var err = new Error(data.description || data.message || 'Something went wrong. Please try again.');
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  /* ---------- state ---------- */
  var state = {
    loaded: false,
    items: {},   /* variantId -> qty (the draft) */
    order: [],   /* variantIds in the order they were first added (slot order) */
    meta: {},    /* variantId -> { t: title, i: image, p: price cents, h: handle, a: accent } */
    plan: '',    /* selling plan id for the whole menu ('' = one time) */
    base: {},    /* cart quantities the draft was built against */
    basePlan: '',
    cartCount: 0
  };

  function cartSnapshot(cart) {
    var map = {};
    var plans = {};
    (cart.items || []).forEach(function (line) {
      var id = String(line.variant_id);
      map[id] = (map[id] || 0) + line.quantity;
      var plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan ? String(line.selling_plan_allocation.selling_plan.id) : '';
      plans[plan] = 1;
      if (!state.meta[id]) {
        state.meta[id] = { t: line.product_title, i: line.image ? line.image + (line.image.indexOf('?') > -1 ? '&' : '?') + 'width=160' : '', p: line.final_price, h: line.handle, a: '' };
      }
    });
    var planKeys = Object.keys(plans);
    return { map: map, plan: planKeys.length === 1 ? planKeys[0] : '' };
  }

  function adopt(cart) {
    var snap = cartSnapshot(cart);
    var stored = readStore();
    if (stored) {
      Object.keys(stored.meta || {}).forEach(function (id) { if (!state.meta[id]) state.meta[id] = stored.meta[id]; });
    }
    if (stored && sameMap(stored.base, snap.map) && (stored.basePlan || '') === snap.plan) {
      /* Cart unchanged since the draft was saved: keep uncommitted edits. */
      state.items = stored.items || {};
      state.order = stored.order || [];
      state.plan = stored.plan || '';
    } else {
      /* Cart changed elsewhere (drawer, checkout, another tab): the cart wins. */
      state.items = Object.assign({}, snap.map);
      state.order = Object.keys(snap.map);
      state.plan = snap.plan;
    }
    state.base = snap.map;
    state.basePlan = snap.plan;
    state.cartCount = cart.item_count || 0;
    state.order = state.order.filter(function (id) { return state.items[id] > 0; });
    Object.keys(state.items).forEach(function (id) { if (state.items[id] > 0 && state.order.indexOf(id) === -1) state.order.push(id); });
    state.loaded = true;
    writeStore();
  }

  var Menu = {
    state: state,
    count: function () { return sum(state.items); },
    qty: function (id) { return state.items[String(id)] || 0; },
    subtotal: function () {
      var base = Object.keys(state.items).reduce(function (n, id) {
        var m = state.meta[id];
        return n + (state.items[id] > 0 && m ? m.p * state.items[id] : 0);
      }, 0);
      var pct = Menu.planPct();
      return pct > 0 ? Math.round(base * (100 - pct) / 100) : base;
    },
    /* Percentage saving of the selected selling plan (data-discount on the frequency radio, rendered from the plan's price adjustment). */
    planPct: function () {
      if (!state.plan) return 0;
      var input = document.querySelector('[data-mk-freq] input[value="' + state.plan + '"]');
      return input ? parseFloat(input.getAttribute('data-discount')) || 0 : 0;
    },
    planName: function () {
      if (!state.plan) return '';
      var input = document.querySelector('[data-mk-freq] input[value="' + state.plan + '"]');
      var span = input && input.parentNode ? input.parentNode.querySelector('span') : null;
      return span ? (span.firstChild && span.firstChild.nodeType === 3 ? span.firstChild.textContent : span.textContent).trim() : '';
    },
    dirty: function () { return !sameMap(state.items, state.base) || (state.plan || '') !== (state.basePlan || ''); },
    remember: function (id, meta) { if (meta) state.meta[String(id)] = Object.assign(state.meta[String(id)] || {}, meta); },
    set: function (id, qty, meta) {
      id = String(id);
      Menu.remember(id, meta);
      qty = Math.max(0, Math.min(qty, 99));
      if (qty > 0) {
        state.items[id] = qty;
        if (state.order.indexOf(id) === -1) state.order.push(id);
      } else {
        delete state.items[id];
        state.order = state.order.filter(function (x) { return x !== id; });
      }
      writeStore();
      emit();
    },
    add: function (id, n, meta) { Menu.set(id, Menu.qty(id) + (n || 1), meta); },
    setPlan: function (plan) { state.plan = plan || ''; writeStore(); emit(); },
    /* Expanded pouch list in slot order, for the five-plate meter. */
    pouches: function () {
      var out = [];
      state.order.forEach(function (id) {
        for (var i = 0; i < (state.items[id] || 0); i++) out.push(id);
      });
      return out;
    },
    on: function (fn) { listeners.push(fn); if (state.loaded) fn(state); },
    refresh: function () { return jsonFetch('cart.js').then(function (cart) { adopt(cart); emit(); return cart; }); },
    commit: commit
  };
  function emit() { listeners.forEach(function (fn) { try { fn(state); } catch (e) { /* keep other views alive */ } }); }

  /* Write the draft to the cart in at most two requests:
     1) /cart/update.js sets every existing line (first line per variant keeps the draft qty, extras go to 0)
     2) /cart/add.js adds variants that are not in the cart yet (with the menu's selling plan). */
  function commit() {
    return jsonFetch('cart.js').then(function (cart) {
      var updates = {};
      var kept = {};
      var plan = state.plan || '';
      (cart.items || []).forEach(function (line) {
        var id = String(line.variant_id);
        var linePlan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan ? String(line.selling_plan_allocation.selling_plan.id) : '';
        var want = state.items[id] || 0;
        if (!kept[id] && linePlan === plan && want > 0) {
          updates[line.key] = want;
          kept[id] = true;
        } else {
          updates[line.key] = 0;
        }
      });
      var adds = [];
      state.order.forEach(function (id) {
        if (!kept[id] && state.items[id] > 0) {
          var item = { id: parseInt(id, 10), quantity: state.items[id] };
          if (plan) item.selling_plan = parseInt(plan, 10);
          adds.push(item);
        }
      });
      var chain = Object.keys(updates).length ? jsonFetch('cart/update.js', { updates: updates }) : Promise.resolve(null);
      return chain.then(function () { return adds.length ? jsonFetch('cart/add.js', { items: adds }) : null; });
    }).then(function () {
      return jsonFetch('cart.js');
    }).then(function (cart) {
      adopt(cart);
      emit();
      announceCart(cart);
      return cart;
    });
  }

  /* Tell the theme: refresh the cart drawer + header bubble, and let mk-theme.js re-run the checkout gate. */
  function announceCart(cart) {
    var drawer = document.querySelector('cart-drawer');
    var sections = ['cart-drawer', 'cart-icon-bubble'];
    fetch(ROOT + '?sections=' + sections.join(','), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (html) {
        if (!html || !drawer) return;
        if (!cart.item_count && typeof drawer.getSectionsToRender === 'function' && typeof drawer.getSectionInnerHTML === 'function') {
          /* Menu cleared: refresh the drawer and bubble quietly instead of opening an empty drawer. */
          drawer.getSectionsToRender().forEach(function (section) {
            var el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            if (el && html[section.id]) el.innerHTML = drawer.getSectionInnerHTML(html[section.id], section.selector);
          });
          drawer.classList.add('is-empty');
          return;
        }
        if (typeof drawer.renderContents === 'function') {
          var first = cart.items && cart.items[0] ? cart.items[0].variant_id : null;
          drawer.renderContents({ id: first, sections: html });
          /* Same clean-up the theme's product form does after an add. */
          drawer.classList.remove('is-empty');
        }
      })
      .catch(function () {});
    try {
      var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
      if (typeof window.publish === 'function' && events && events.cartUpdate) {
        window.publish(events.cartUpdate, { source: 'mk-menu', cartData: cart });
      }
    } catch (e) { /* no pub/sub on this page */ }
  }

  /* ---------- shared view: the five plate slots ---------- */
  function renderSlots(el) {
    var min = parseInt(el.getAttribute('data-min'), 10) || MIN || 5;
    var pouches = Menu.pouches();
    var slots = Array.prototype.slice.call(el.querySelectorAll('i'));
    while (slots.length < min) { var i = document.createElement('i'); el.insertBefore(i, el.querySelector('.mk-slots__more')); slots.push(i); }
    slots.forEach(function (slot, n) {
      var id = pouches[n];
      var was = slot.getAttribute('data-id') || '';
      if (id) {
        var m = state.meta[id] || {};
        slot.classList.add('is-filled');
        slot.style.setProperty('--slot', m.a || 'var(--mk-gingham)');
        slot.style.setProperty('--slot-img', m.i ? 'url("' + m.i + '")' : 'none');
        slot.setAttribute('data-id', id);
        if (was !== id && el.__mkSeen) { slot.classList.remove('is-pop'); void slot.offsetWidth; slot.classList.add('is-pop'); }
      } else {
        slot.classList.remove('is-filled', 'is-pop');
        slot.style.removeProperty('--slot');
        slot.style.removeProperty('--slot-img');
        slot.removeAttribute('data-id');
      }
    });
    var more = el.querySelector('.mk-slots__more');
    if (more) {
      var extra = pouches.length - min;
      more.hidden = extra <= 0;
      more.textContent = extra > 0 ? '+' + extra : '';
    }
    el.classList.toggle('is-complete', pouches.length >= min);
    el.__mkSeen = true;
  }

  /* ---------- toast ---------- */
  var toastTimer = null;
  function toast(id, title, sub) {
    var el = document.querySelector('[data-mk-toast]');
    if (!el) return;
    var m = state.meta[id] || {};
    el.innerHTML = '';
    var dot = document.createElement('i');
    if (m.a) dot.style.setProperty('--slot', m.a);
    if (m.i) dot.style.setProperty('--slot-img', 'url("' + m.i + '")');
    var text = document.createElement('span');
    text.appendChild(document.createTextNode(title));
    if (sub) { var s = document.createElement('small'); s.textContent = sub; text.appendChild(s); }
    if (id) el.appendChild(dot);
    el.appendChild(text);
    el.hidden = false;
    window.requestAnimationFrame(function () { el.classList.add('is-visible'); });
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      el.classList.remove('is-visible');
      window.setTimeout(function () { el.hidden = true; }, 320);
    }, 3200);
  }
  function progressLine(count) {
    if (MIN > 0 && count < MIN) return fill(LABELS.under || MK.minMessage || 'Add [remaining] more to reach the [min]-pouch minimum.', { remaining: MIN - count, min: MIN, count: count });
    return LABELS.ready || 'Your menu is ready. Add more anytime.';
  }

  /* ---------- Build page ---------- */
  function initBuilder(section) {
    if (section.__mkMenu) return;
    section.__mkMenu = true;
    var cards = Array.prototype.slice.call(section.querySelectorAll('[data-mk-card]'));
    var summary = section.querySelector('[data-mk-summary]');
    var commitButtons = Array.prototype.slice.call(section.querySelectorAll('[data-mk-commit]'));
    var errorEl = section.querySelector('[data-mk-error]');
    var busy = false;

    function cardMeta(card) {
      return {
        t: card.getAttribute('data-title'),
        i: card.getAttribute('data-image'),
        p: parseInt(card.getAttribute('data-price'), 10) || 0,
        h: card.getAttribute('data-handle'),
        a: card.getAttribute('data-accent') || ''
      };
    }
    function available(card) { return card.getAttribute('data-available') !== 'false'; }
    function maxFor(card) { return parseInt(card.getAttribute('data-max'), 10) || 99; }
    cards.forEach(function (card) { Menu.remember(card.getAttribute('data-variant-id'), cardMeta(card)); });

    cards.forEach(function (card) {
      var id = card.getAttribute('data-variant-id');
      function change(delta) {
        if (delta > 0 && !available(card)) return;
        var next = Math.min(maxFor(card), Menu.qty(id) + delta);
        Menu.set(id, next, cardMeta(card));
        hideError();
      }
      var add = card.querySelector('[data-mk-add]');
      var inc = card.querySelector('[data-mk-inc]');
      var dec = card.querySelector('[data-mk-dec]');
      if (add) add.addEventListener('click', function () {
        change(1);
        var focusTarget = card.querySelector('[data-mk-inc]');
        if (focusTarget) window.requestAnimationFrame(function () { focusTarget.focus(); });
      });
      if (inc) inc.addEventListener('click', function () { change(1); });
      if (dec) dec.addEventListener('click', function () {
        change(-1);
        if (Menu.qty(id) === 0 && add) window.requestAnimationFrame(function () { add.focus(); });
      });
    });

    var freq = section.querySelector('[data-mk-freq]');
    if (freq) {
      freq.addEventListener('change', function (event) {
        if (event.target && event.target.name) Menu.setPlan(event.target.value);
      });
    }
    /* Only keep a stored frequency if this page still offers it (plans can be removed in the app). */
    Menu.ready.then(function () {
      var offered = freq ? Array.prototype.map.call(freq.querySelectorAll('input'), function (i) { return i.value; }) : [''];
      if (offered.indexOf(state.plan || '') === -1) Menu.setPlan('');
    });

    function hideError() { if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; } }
    function showError(msg) { if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; } }

    function ctaState() {
      var count = Menu.count();
      var dirty = Menu.dirty();
      var total = money(Menu.subtotal());
      if (dirty && count === 0) return { enabled: true, label: 'Update cart', short: 'Update cart', action: 'commit' };
      if (MIN > 0 && count < MIN) {
        var left = MIN - count;
        return count === 0
          ? { enabled: false, label: 'Add ' + MIN + ' pouches to check out', short: 'Add ' + MIN + ' to start', action: 'none' }
          : { enabled: false, label: 'Add ' + left + ' more to check out', short: 'Add ' + left + ' more', action: 'none' };
      }
      if (dirty) {
        return state.cartCount > 0
          ? { enabled: true, label: 'Update cart · ' + total, short: 'Update cart', action: 'commit' }
          : { enabled: true, label: 'Add to cart · ' + total, short: 'Add to cart', action: 'commit' };
      }
      return { enabled: true, label: 'Check out →', short: 'Check out →', action: 'checkout' };
    }

    function render() {
      var count = Menu.count();
      cards.forEach(function (card) {
        var id = card.getAttribute('data-variant-id');
        var qty = Menu.qty(id);
        var add = card.querySelector('[data-mk-add]');
        var step = card.querySelector('[data-mk-step]');
        var badge = card.querySelector('[data-mk-card-badge]');
        var out = card.querySelector('[data-mk-card-qty]');
        var inc = card.querySelector('[data-mk-inc]');
        card.classList.toggle('is-in-menu', qty > 0);
        if (add) add.hidden = qty > 0 || !available(card);
        if (step) step.hidden = qty === 0;
        if (out) out.textContent = qty;
        if (inc) inc.disabled = qty >= maxFor(card) || !available(card);
        if (badge) { badge.hidden = qty === 0; badge.textContent = '×' + qty; }
      });
      if (summary) {
        var countEl = summary.querySelector('[data-mk-count]');
        var unit = summary.querySelector('[data-mk-unit]');
        var msg = summary.querySelector('[data-mk-msg]');
        var lines = summary.querySelector('[data-mk-lines]');
        var totalRow = summary.querySelector('[data-mk-total-row]');
        var subtotal = summary.querySelector('[data-mk-subtotal]');
        var fine = summary.querySelector('[data-mk-fine]');
        if (countEl) countEl.textContent = count;
        if (unit) unit.textContent = plural(count, unit.getAttribute('data-one'), unit.getAttribute('data-many'));
        if (msg) {
          var dirty = Menu.dirty();
          if (count === 0 && dirty) msg.textContent = 'Your cart still has pouches in it. Update your cart to clear them out.';
          else if (count === 0) msg.textContent = LABELS.empty || 'Start with any five pouches. Mix proteins however you like.';
          else if (MIN > 0 && count < MIN) msg.textContent = progressLine(count);
          else if (dirty) msg.textContent = 'Your menu is ready. Add it to your cart to check out.';
          else msg.textContent = 'In your cart and ready to check out. Change anything above.';
        }
        if (lines) {
          lines.innerHTML = '';
          state.order.forEach(function (id) {
            var qty = state.items[id];
            if (!qty) return;
            var m = state.meta[id] || {};
            var li = document.createElement('li');
            var dot = document.createElement('span');
            if (m.a) dot.style.setProperty('--slot', m.a);
            if (m.i) dot.style.setProperty('--slot-img', 'url("' + m.i + '")');
            var name = document.createElement('span');
            var b = document.createElement('b'); b.textContent = m.t || 'Pouch';
            var small = document.createElement('small'); small.textContent = qty + ' × ' + money(m.p || 0);
            name.appendChild(b); name.appendChild(small);
            var price = document.createElement('em'); price.textContent = money((m.p || 0) * qty);
            li.appendChild(dot); li.appendChild(name); li.appendChild(price);
            lines.appendChild(li);
          });
        }
        if (totalRow) totalRow.hidden = count === 0;
        if (subtotal) subtotal.textContent = money(Menu.subtotal());
        if (fine) {
          var pct = Menu.planPct();
          var fineBase = fine.getAttribute('data-fine-base') || fine.textContent;
          fine.setAttribute('data-fine-base', fineBase);
          fine.innerHTML = state.plan
            ? 'Delivers ' + (Menu.planName() ? Menu.planName().toLowerCase() : 'on your schedule') + (pct > 0 ? ' · ' + pct + '% off applied' : '') + '. <b>' + fineBase + '</b>'
            : fineBase;
        }
        Array.prototype.forEach.call(summary.querySelectorAll('[data-mk-freq] input'), function (input) { input.checked = input.value === (state.plan || ''); });
      }
      Array.prototype.forEach.call(section.querySelectorAll('[data-mk-slots]'), renderSlots);
      var trayCount = section.querySelector('[data-mk-tray-count]');
      if (trayCount) {
        trayCount.textContent = MIN > 0 && count < MIN ? count + ' of ' + MIN + ' pouches' : count + ' ' + plural(count, 'pouch', 'pouches') + ' · ' + money(Menu.subtotal());
      }
      var s = ctaState();
      commitButtons.forEach(function (btn) {
        btn.disabled = !s.enabled || busy;
        btn.classList.toggle('is-busy', busy);
        btn.setAttribute('data-action', s.action);
        var label = btn.querySelector('[data-mk-commit-label]') || btn;
        label.textContent = busy ? 'Updating…' : (btn.classList.contains('mk-tray__cta') ? s.short : s.label);
      });
    }

    commitButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var action = btn.getAttribute('data-action');
        if (busy || action === 'none') return;
        if (action === 'checkout') { window.location.href = MK.checkoutUrl || ROOT + 'checkout'; return; }
        busy = true;
        hideError();
        render();
        commit().then(function () {
          busy = false;
          closeSheet();
          render();
        }).catch(function (err) {
          busy = false;
          showError(err.message);
          openSheet();
          render();
        });
      });
    });

    /* Mobile tray: open the summary as a bottom sheet */
    var tray = section.querySelector('[data-mk-tray]');
    var opener = section.querySelector('[data-mk-tray-open]');
    var lastFocus = null;
    function openSheet() {
      if (!tray || window.matchMedia('(min-width: 990px)').matches) return;
      lastFocus = document.activeElement;
      section.classList.add('is-sheet-open');
      document.body.classList.add('mk-sheet-lock');
      if (opener) opener.setAttribute('aria-expanded', 'true');
      var close = summary && summary.querySelector('.mk-menu-summary__close');
      if (close) window.setTimeout(function () { close.focus(); }, 60);
    }
    function closeSheet() {
      if (!section.classList.contains('is-sheet-open')) return;
      section.classList.remove('is-sheet-open');
      document.body.classList.remove('mk-sheet-lock');
      if (opener) opener.setAttribute('aria-expanded', 'false');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    if (opener) opener.addEventListener('click', openSheet);
    Array.prototype.forEach.call(section.querySelectorAll('[data-mk-tray-close]'), function (el) { el.addEventListener('click', closeSheet); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeSheet(); });
    if (tray) document.body.classList.add('mk-has-tray');

    Menu.on(render);

    /* A protein that sold out (or is not released yet) cannot count toward the minimum: take it off the menu and say so. */
    Menu.ready.then(function () {
      var gone = cards.filter(function (card) { return !available(card) && Menu.qty(card.getAttribute('data-variant-id')) > 0; });
      if (!gone.length) return;
      gone.forEach(function (card) { Menu.set(card.getAttribute('data-variant-id'), 0, cardMeta(card)); });
      var first = gone[0].getAttribute('data-variant-id');
      if (gone.length === 1) toast(first, gone[0].getAttribute('data-title') + ' isn’t available right now', 'We took it off your menu.');
      else toast(first, gone.length + ' proteins aren’t available right now', 'We took them off your menu.');
    });

    /* ?add=<handle> from a product page: add one pouch, then clean the URL so a refresh does not add again. */
    Menu.ready.then(function () {
      var params = new URLSearchParams(window.location.search);
      var handle = params.get('add');
      if (!handle) return;
      params.delete('add');
      var clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
      window.history.replaceState(null, '', clean);
      var card = section.querySelector('[data-mk-card][data-handle="' + handle.replace(/[^a-z0-9-]/gi, '') + '"]');
      if (!card) return;
      var id = card.getAttribute('data-variant-id');
      var title = card.getAttribute('data-title');
      if (!available(card)) { toast(id, title + ' is coming soon.', 'Start your menu with what’s ready now.'); return; }
      Menu.add(id, 1, cardMeta(card));
      var count = Menu.count();
      toast(id, 'Added ' + title, MIN > 0 && count < MIN ? (MIN - count) + ' more to reach ' + MIN : count + ' pouches in your menu');
      card.classList.remove('is-flash'); void card.offsetWidth; card.classList.add('is-flash');
      var rect = card.getBoundingClientRect();
      var headerH = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue('--mk-header-h'), 10) || 86;
      if (rect.top < headerH || rect.bottom > window.innerHeight) {
        window.scrollTo({ top: Math.max(0, window.pageYOffset + rect.top - headerH - 20), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    });
  }

  /* ---------- Product page: menu card + sticky bar text ---------- */
  function initProductMenu(root) {
    if (root.__mkMenu) return;
    root.__mkMenu = true;
    var pdp = root.closest('[data-mk-pdp]') || document;
    var id = pdp.getAttribute && pdp.getAttribute('data-variant-id');
    Menu.on(function () {
      var count = Menu.count();
      var mine = id ? Menu.qty(id) : 0;
      var countText = count === 0 ? 'Orders start at ' + MIN + ' pouches'
        : (MIN > 0 && count < MIN ? count + ' of ' + MIN + ' pouches' : count + ' ' + plural(count, 'pouch', 'pouches') + ' · minimum met');
      Array.prototype.forEach.call(document.querySelectorAll('[data-mk-menu-count-text]'), function (el) { el.textContent = countText; });
      Array.prototype.forEach.call(document.querySelectorAll('[data-mk-menu-count-short]'), function (el) {
        el.textContent = count === 0 ? 'Orders start at ' + MIN : (count < MIN ? count + ' of ' + MIN + ' in your menu' : count + ' in your menu');
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-mk-slots]'), renderSlots);
      var label = root.querySelector('[data-mk-pdp-add-label]');
      if (label) label.textContent = mine > 0 ? 'Add another to your menu' : 'Add to your menu';
      var note = root.querySelector('[data-mk-pdp-note]');
      if (note) {
        if (count === 0) note.textContent = 'Mix any proteins. Check out once your menu has five or more pouches.';
        else if (MIN > 0 && count < MIN) note.textContent = (mine > 0 ? mine + ' already in your menu. ' : '') + progressLine(count);
        else note.textContent = (mine > 0 ? mine + ' in your menu. ' : '') + 'Your menu is ready to check out.';
      }
    });
  }

  /* ---------- boot ---------- */
  Menu.ready = jsonFetch('cart.js').then(function (cart) { adopt(cart); }).catch(function () {
    /* Cart unavailable (offline / editor): fall back to the stored draft. */
    var stored = readStore();
    if (stored) { state.items = stored.items || {}; state.order = stored.order || []; state.meta = stored.meta || {}; state.plan = stored.plan || ''; state.base = stored.base || {}; }
    state.loaded = true;
  }).then(function () { emit(); });

  /* Keep the draft honest when the cart changes in the drawer or another tab. */
  document.addEventListener('visibilitychange', function () { if (!document.hidden && state.loaded) Menu.refresh().catch(function () {}); });
  try {
    var ev = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
    if (typeof window.subscribe === 'function' && ev && ev.cartUpdate) {
      window.subscribe(ev.cartUpdate, function (payload) { if (!payload || payload.source !== 'mk-menu') Menu.refresh().catch(function () {}); });
    }
  } catch (e) { /* no pub/sub */ }

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-mk-menu]'), initBuilder);
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-mk-pdp-menu]'), initProductMenu);
  }
  window.MKMenu = Menu;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
